<script>
  import { onMount } from 'svelte';
  import { getLoggedIn, getCartCount, getCurrentUrl } from '../../../../../js/stores';
  import { shouldRenderCta, shouldRenderSecondaryCta, filterNavItems } from '../../../../../js/navItemFilters';

  import ButtonLink from '../../../ButtonLink/ButtonLink.svelte';
  import HeaderMenu from './HeaderMenu/HeaderMenu.svelte';

  const loggedInStore = getLoggedIn();
  const cartCountStore = getCartCount();
  const currentUrlStore = getCurrentUrl();

  export let data = {};
  export let isSticky = false;
  export let hiddenUntilScrolled = false;

  // headerHeight is bound to header height below
  // and used to size the shim element
  let headerHeight;

  // navWidth is bound to nav width below
  // and used to set the width of the header & footer
  // (which they need when they are position fixed)
  let navWidth;

  let ctaHasFocus = false;

  const disableIconCtaOnMobile = Boolean(data.disableIconCtaOnMobile);

  // Determine whether or not to render CTA
  $: renderCta = shouldRenderCta({
    data,
    currentUrl: $currentUrlStore,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
  });

  // Filtered CTA item
  $: filteredCta = renderCta ? data.cta : undefined;

  // Determine whether or not to render Secondary CTA
  $: renderSecondaryCta = shouldRenderSecondaryCta({
    data,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
  });

  // Filtered Secondary CTA item
  $: filteredSecondaryCta = renderSecondaryCta ? data.secondaryCta : undefined;

  // Filtered nav items
  $: filteredNavItems = filterNavItems({
    items: data.navItems,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
  });

  let ctaSubnavMobileHeader;
  onMount(() => {
    // Resetting the position styling fixes the sticky nav not 'sticking' caused by deferred JS
    // Context: https://jira.sqprod.co/browse/WEBENG-4190
    if (ctaSubnavMobileHeader) {
      ctaSubnavMobileHeader.style.position = '';
    }
  });

  function onCtaFocus() {
    ctaHasFocus = true;
  }
  function onCtaBlur() {
    ctaHasFocus = false;
  }
</script>

<nav class="CtaSubnavMobileNav" bind:offsetWidth={navWidth}>
  {#if data.title}
    {#if isSticky}
      <div class="shim" aria-hidden="true" style="height: {headerHeight}px;"></div>
    {/if}
    <div
      class="header"
      class:isSticky
      class:hiddenUntilScrolled
      style="width: {navWidth}px;"
      bind:offsetHeight={headerHeight}
      bind:this={ctaSubnavMobileHeader}
    >
      <HeaderMenu title={data.title} navItems={filteredNavItems} />
    </div>
  {/if}
  {#if filteredSecondaryCta || filteredCta}
    <div
      class="footer"
      class:isSticky
      class:isVisible={isSticky || ctaHasFocus}
      class:disableLeftCtaIcon={disableIconCtaOnMobile}
      style="width: {navWidth}px;"
    >
      {#if filteredSecondaryCta}
        <ButtonLink
          id={filteredSecondaryCta.id}
          icon={filteredSecondaryCta.iconData}
          title={filteredSecondaryCta.title}
          url={filteredSecondaryCta.url}
          linkStyle={filteredSecondaryCta.linkStyle}
        />
      {/if}
      {#if filteredCta}
        <ButtonLink
          id={filteredCta.id}
          title={filteredCta.title}
          url={filteredCta.url}
          linkStyle="primary"
          on:focus={onCtaFocus}
          on:blur={onCtaBlur}
        />
      {/if}
    </div>
  {/if}
</nav>

<style>.CtaSubnavMobileNav {
  display: block;
}
@media (min-width: 1280px) {
  .CtaSubnavMobileNav {
    display: none;
  }
}

.shim {
  background-color: var(--color-background, #fff);
}

.header,
.footer {
  border-width: 0;
  border-style: solid;
  color: var(--color-text, #1a1a1a);
  background-color: var(--color-background, #fff);
  border-color: var(--color-border, #d9d9d9);
  box-sizing: border-box;
  position: relative;
  min-width: 320px;
  border-bottom-width: 1px;
  max-width: 100%;
}

.header.isSticky {
  position: fixed;
  top: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
}
.header.isSticky.hiddenUntilScrolled {
  transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
  transform: translateY(0);
}

.footer {
  position: fixed;
  bottom: 0;
  display: flex;
  gap: 15px;
  padding: 10px 25px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.16);
  transform: translateY(100%);
  transition: transform 75ms cubic-bezier(0.4, 0, 1, 1);
}

:global(.footer) :global(.ButtonLink) :global(.title) {
  display: none;
}
:global(.footer) :global(.ButtonLink:last-child) {
  flex: 1;
}
:global(.footer) :global(.ButtonLink:last-child) :global(.title) {
  display: inline;
}
@media (max-width: 717px) {
  :global(.footer) :global(.ButtonLink:last-child) :global(.icon) + :global(.title) {
    margin-left: 5px;
  }
}
@media (min-width: 718px) {
  :global(.footer) :global(.ButtonLink) {
    flex: 1;
  }
  :global(.footer) :global(.ButtonLink) :global(.icon) {
    display: none;
  }
  :global(.footer) :global(.ButtonLink) :global(.title) {
    display: inline;
  }
}
:global(.footer.disableLeftCtaIcon) {
  flex-flow: row-reverse wrap-reverse;
  gap: 7px;
}
:global(.footer.disableLeftCtaIcon) :global(.ButtonLink:last-child) {
  flex: 2 1 auto;
}
:global(.footer.disableLeftCtaIcon) :global(.ButtonLink:first-child) {
  flex: 1 1 auto;
}
@media (min-width: 718px) {
  :global(.footer.disableLeftCtaIcon) :global(.ButtonLink:first-child),
  :global(.footer.disableLeftCtaIcon) :global(.ButtonLink:last-child) {
    flex: 1;
  }
}
:global(.footer.disableLeftCtaIcon) :global(.ButtonLink) :global(.title) {
  display: inline;
}
:global(.footer.disableLeftCtaIcon) :global(.ButtonLink) :global(.icon) {
  display: none;
}

.hiddenUntilScrolled {
  position: fixed;
  z-index: 2;
  top: 0;
  transition: transform 200ms cubic-bezier(0.4, 0, 1, 1);
  transform: translateY(-100%);
}

.footer.isVisible {
  transform: translateY(0%);
  transition: transform 150ms cubic-bezier(0, 0, 0.2, 1);
}</style>
